import React, { useEffect, useMemo, useState } from 'react';
import { Button, IconButton, Form, Modal, Schema, SelectPicker, Loader } from 'rsuite';
import ReloadIcon from '@rsuite/icons/Reload';
import { CurrencyField, Field } from '../../../../helpers/rsuite/Form';
import { disableInput } from '../../../../helpers/styles/Inputs';
import { cuotitasStoreService } from '../../../../services';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@material-ui/core';

const body0 = {
    width: '540px',
    padding: '0 4px'
};

const required = 'Este campo es requerido.';
const msgNumber = 'Por favor, ingrese un número valido.';
const msgString = 'Por favor, ingrese una opción.';

const { NumberType, StringType } = Schema.Types;

const InstallmentsStep = (context) => {
    const [selectedValue, setSelectedValue] = useState('');
    const [loadingInstallments, setLoadingInstallments] = useState(false);
    const [amountTarget, setAmountTarget] = useState('');
    const [showFeeValues, setShowFeeValues] = useState(false);
    const [idsPlan, setIdsPlan] = useState([]);
    const [installmentsList, setInstallmentsList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchInstallmentsByOffer = async (amount) => {
        setLoadingInstallments(true);
        const offer = context.offer?.id || context.formInstallments.id_loan_offer;
        try {
            const response = await cuotitasStoreService.getInstallmentsByOffer(context.idStore, offer, amount);
            localStorage.setItem('assistance', JSON.stringify(response?.assistanceMapping));
            setIdsPlan(response?.idsPlan);
            setInstallmentsList(response?.installmentsList);
        }
        catch (error) {
            console.error('Error al obtener las cuotas:', error);
        }
        finally {
            setLoadingInstallments(false);
        }
    };

    const handleAmountChange = (value) => {
        setShowFeeValues(false);
        setAmountTarget(value);
    };

    const getFeeValues = async () => {
        setIsLoading(true);
        setSelectedValue('');
        context.setFormInstallments((prev) => ({ ...prev, amount: amountTarget }));
        await fetchInstallmentsByOffer(amountTarget);
        setIsLoading(false);
        setShowFeeValues(true);
    };

    const installmentAllowed = useMemo(() => {
        return installmentsList.filter(i => i.idPlanName === selectedValue);
    }, [installmentsList, selectedValue, amountTarget]);

    const model = Schema.Model({
        amount: NumberType(msgNumber)
            .addRule((value, data) => {
                const amount = value * 1;
                if (!amount || amount < 0) return false;
                return true;
            }, 'El monto ingresado no es válido')
            .addRule((value, data) => {
                const amount = value * 1;
                if (isLessThanMin(amount)) return false;
                return true;
            }, 'El monto seleccionado no puede ser menor al mínimo permitido')
            .addRule((value, data) => {
                const amount = value * 1;
                if (isGreaterThanMax(amount)) return false;
                return true;
            }, 'El monto seleccionado no puede ser mayor a la oferta')
            .isRequired(`${required}`),
        installments: StringType(msgString)
            .isRequired(`${required}`)
    });

    const handleChange = (event) => {
        setSelectedValue(event.target.value);
    };

    const isLessThanMin = (amount) => {
        return amount < context?.amountLimit?.amountMinByPerson || amount < context?.amountLimit?.amountMinOfInstallments;
    };

    const isGreaterThanMax = (amount) => {
        return amount > context?.amountLimit?.amountMaxByPerson || amount > context?.amountLimit?.amountMaxOfInstallments;
    };

    const disabledGetFeeValues = () => {
        return !!(isLessThanMin(amountTarget) || isGreaterThanMax(amountTarget));
    };

    const submit = () => {
        if (!context.formRef.current.check()) {
            console.error('Form Error');
            return;
        }
        context.next();
    };

    const back = () => {
        if (context.politicies.length === 1) {
            context.customStep(1, 0);
        }
        else {
            context.back();
        }
    };

    useEffect(() => {
        if (idsPlan.length === 1 && !selectedValue) {
            setSelectedValue(idsPlan[0]);
        }
    }, [idsPlan, selectedValue]);

    useEffect(() => {
        setAmountTarget('');
        context.setFormInstallments((value) => ({
            ...value,
            amount: '',
            installments: []
        }));
    }, []);

    return (
        <>
            <Modal.Body className='mb-5 h-100 py-1' style={{ height: '540px', overflow: 'show' }}>
                <div className='row m-0' style={body0} />
                <Form
                    fluid
                    formValue={context.formInstallments}
                    model={model}
                    onChange={context.setFormInstallments}
                    ref={context.formRef}
                    onCheck={context.setFormErrorInstallments}
                >
                    <Field classNameGroup='mb-3' classNameLabel='font-weight-bold' name='maxAmount' label="Monto máximo"
                        value={context.amountLimit?.amountMaxByPerson}
                        accepter={CurrencyField}
                        displayType={'text'}
                        readOnly
                        style={disableInput}
                    />
                    <Field classNameGroup='mb-3' classNameLabel='font-weight-bold' name='amount' label="Montos"
                        accepter={CurrencyField}
                        error={context.setFormErrorMainUser.amount}
                        onChange={handleAmountChange}
                    />
                    {(isLoading || !showFeeValues) && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-start', width: '100%' }}>
                        <span style={{ fontWeight: 500 }}>Mostrar productos y cuotas</span>
                        <IconButton icon={<ReloadIcon style={{ color: 'blue' }}/>}
                            disabled={context.isLoading || !amountTarget || disabledGetFeeValues()}
                            onClick={() => getFeeValues()}/>
                    </div>}
                    {isLoading && (<Loader size="md" content="Calculando productos y cuotas..." />)}
                    {showFeeValues && <FormControl component="fieldset" style={{ width: '100%' }}>
                        <span style={{ fontWeight: 500 }}>Producto</span>
                        <RadioGroup value={selectedValue} onChange={handleChange} aria-label="" row>
                            {idsPlan.map((products, index) => (
                                <FormControlLabel
                                    key={index}
                                    value={String(products)}
                                    control={<Radio />}
                                    label={products}
                                    style={{ width: index % 2 === 0 ? '50%' : '45%' }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>}
                    { showFeeValues && <Field classNameGroup='mb-3' classNameLabel='font-weight-bold' name="installments" label="Cuotas"
                        accepter={SelectPicker}
                        data={installmentAllowed}
                        style={{ width: '100%' }}
                        placeholder="Seleccionar"
                    />
                    }
                </Form>
            </Modal.Body>
            <Modal.Footer className='mt-4'>
                <div>
                    <Button appearance='subtle' id='back_btn' disabled={context.isLoading || loadingInstallments} onClick={() => back()} >Atrás</Button>
                    <Button appearance='primary' id='third_forward' disabled={context.isLoading || loadingInstallments} onClick={() => submit()} >Siguiente</Button>
                </div>
            </Modal.Footer>
        </>
    );
};

export default InstallmentsStep;
